import { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Box, Collapse, Paper, Tab, Tabs } from "@mui/material";

import ForumIcon			from '@mui/icons-material/Forum';
import ChevronRightIcon		from '@mui/icons-material/ChevronRight';
import LightbulbIcon 		from '@mui/icons-material/Lightbulb';
import PersonIcon			from "@mui/icons-material/Person";

import PanelMain 			from "./components/panelMain";
import PanelReviews			from "./components/panelReviews";
import PanelForum 			from "./components/panelForum";
import PanelUserParameters 	from "./components/panelUserParameters";

import CompanyContext 		from "./context";
import useAuth 				from "../../hooks/useAuth";
import useFetch 			from "../../api/api";
import useExternalFetch 	from "../../api/apiExternal";
import useBreakpoint 		from "../../hooks/useBreakpoint";

import GeneralError 		from "../../components/GeneralError";
import config 				from "../../common/Config";

const Company = () => {
	const {ticker}									= useParams()
	const {auth}									= useAuth()

	const [company, setCompany]						= useState()
	const [stocks, setStocks]						= useState()
	const [currency, setCurrency]					= useState()
	const [exchangeRate, setExchangeRate]			= useState()
	const [selectedStock, setSelectedStock]			= useState()
	const [indicators, setIndicators]				= useState()
	const [tickerNotFound, setTickerNotFound]		= useState(false)

	const [currentTabIndex, setCurrentTabIndex] 	= useState(0);
	const [isNotesOpen, setIsNotesOpen] 			= useState(false)
	const [isLogged, setIsLogged]					= useState(auth.userId ? true : false)
	const [portfolioVersion, setPortfolioVersion]	= useState(0)
	
	const privateFetch								= useFetch()
	const externalFetch								= useExternalFetch()
	const { hash }									= useLocation()
	const currentBreakPoint							= useBreakpoint()

	useEffect(() => {
		setIsLogged(auth.userId ? true : false)
	}, [auth])

	useEffect(() => {
		 // Se alterou a companhia, remove o ticker selecionado
		setSelectedStock(null)

		// Verifica se foi passado uma âncora e, se sim, abre a tab correspondente
		if (hash === '#forum') {
			setCurrentTabIndex(1)
			setIsNotesOpen(true)
		}
		else if (hash === '#reviews') {
			setCurrentTabIndex(0)
			setIsNotesOpen(true)
		}
		else {
			setCurrentTabIndex(0)
			setIsNotesOpen(false)
		};

		// Recupera dados gerais da empresa
		let promiseCompany = privateFetch(config.apiUrl +"/api/company/"+ ticker)
		.then((data) => {
			if (data.status !== 200) throw Error("Ticker not found!")
			return data.json()
		})
		.then((data) => {
			// Reforça ordenamento do array
			data.financialReports.sort( (a,b) => a.year < b.year ? 1 : -1)
			data.financialQuarterReports.sort( (a,b) => a.year < b.year ? 1 : -1)

			setCompany(data)
			return data
		})
		.catch(e => setTickerNotFound(true))

		// Recupera dados de cada ticker negociado pela empresa
		let promiseStocks = privateFetch(config.apiUrl +"/api/company/"+ ticker +'/stocks')
		.then((data) => {
			if (data.status !== 200) throw Error("Ticker not found!")
			return data.json()
		})
		.then((data) => {
			setStocks(data)
			return data
		})
		.catch(e => setTickerNotFound(true))

		// Aguarda buscar os dados da companhia e dos seus tickers
		Promise.all([promiseCompany, promiseStocks])
		.then(async res => {

			let currentCompany 		= res[0]
			let currentStocks		= res[1]

			if (!currentCompany || !currentStocks) return 

			// Seta a moeda atual
			let currentCurrency 	= currentCompany.financialReports[0].currency ? currentCompany.financialReports[0].currency : 'BRL'
			setCurrency(currentCurrency)

			let currentExchangeRate

			// Caso a moeda nao seja DOLAR, busca a taxa de conversao
			if (currentCurrency !== 'USD') {
				 await externalFetch('https://financialmodelingprep.com/api/v3/quote/'+ currentCurrency +'USD')
				.then(data => data.json())
				.then(data => {
					currentExchangeRate = data[0].price
					setExchangeRate(currentExchangeRate)
				})
			}
			else {
				currentExchangeRate = 1
				setExchangeRate(currentExchangeRate)
			}

			setSelectedStock(ticker.toUpperCase())
		})
	}, [ticker])

	// Calcula os indicadores da empresa
	useEffect(() => {
		if (!selectedStock) return
		
		privateFetch(config.apiUrl +'/api/indicators', {
			method 				: 'POST',
			headers 			: {'content-type' : 'application/json; charset=utf-8'},
			body : JSON.stringify({
				ticker 			: selectedStock,
				currency		: company.currency,
				industry 		: company.industry,
				index 			: company.country == 'BR' ? 'IBOV' : 'SP500'
			})		
		})
		.then(results => results.json())
		.then(results => setIndicators(results))
	}, [selectedStock])

	const handleTabChange = (e, tabIndex) => {
		if (tabIndex < 4) setCurrentTabIndex(tabIndex);
	};

	if (tickerNotFound) return (<GeneralError height='88vh' message="Ticker não econtrado!"/>)
	
	return (
		<CompanyContext.Provider value={{
			company				: company, 
			stocks				: stocks, 
			currency			: currency,
			exchangeRate		: exchangeRate,
			indicators			: indicators,
			selectedStock 		: selectedStock,
			setSelectedStock 	: setSelectedStock,
			isNotesOpen			: isNotesOpen,
			portfolioVersion	: portfolioVersion,
			setPortfolioVersion	: setPortfolioVersion,
			}}>
			<Paper sx={{martinTop:0, martinBottom:4, marginLeft: 2, marginRight: 4, height:'88vh', display: 'flex' }} elevation={1}>
				
				{/* Area principal, corresponde a 2/3 da area diponivel */}
				<Box sx={{width: '100%', display:'flex', marginLeft: 2, marginTop: 1, overflowX: 'auto'}}>
					<PanelMain />
				</Box>

				{/* Se o tamanho da tela é menor que MD não mostra os controles laterais */ 
				currentBreakPoint('SIZE') > 2 ? (
					<>
					{/* Menu lateral direito que permite visualizar anotacoes e o forum */}
					<Tabs
						orientation="vertical"
						value={currentTabIndex}
						onChange={handleTabChange}
						sx={{ borderRight: 1, borderColor: 'divider', minWidth: 60 }}
						onClick={() => !isNotesOpen && isLogged ? setIsNotesOpen(true) : null}
						>
						<Tab icon={<LightbulbIcon/>} style={{padding: 0, minWidth:50}} disabled={!isLogged}/>
						<Tab icon={<ForumIcon/>}  style={{padding: 0, minWidth:50}} disabled={!isLogged}/>
						<Tab icon={<PersonIcon/>}  style={{padding: 0, minWidth:50}} disabled={!isLogged}/>
						{isNotesOpen ? (<Tab icon={<ChevronRightIcon/>} style={{padding: 0, minWidth:50}} onClick={() => setIsNotesOpen(false)}/>) : null }
					</Tabs>

					{/* Area secundaria, corresponde a 1/3 da area disponivel */}
					<Collapse 
						unmountOnExit
						orientation="horizontal" 
						in={isNotesOpen} 
						sx={{
							// display: 'flex', 
							flexDirection: "column-reverse", 
							width: '100% !important', 
							maxWidth:1/3, 
							'& .MuiCollapse-wrapperInner' : {
								width: '100%'
							}
						}}>
						<Box sx={{height: '96%', overflowX: 'auto'}}>
							{ currentTabIndex === 0 ? <PanelReviews/> : null }
							{ currentTabIndex === 1 ? <PanelForum/> : null }
							{ currentTabIndex === 2 ? <PanelUserParameters/> : null }
						</Box>
					</Collapse>
					</>
				) : null}
			</Paper>
		</CompanyContext.Provider>	
	)
}

export default Company;